export type CrewJobPayUnit = 'hour' | 'day';

export type CrewJobPayBand = {
  unit: CrewJobPayUnit;
  min: number;
  max: number;
};

export type CrewJobStatus = 'draft' | 'released' | 'filled' | 'closed';

export const CREW_JOB_STATUSES: CrewJobStatus[] = ['draft', 'released', 'filled', 'closed'];

export const CREW_JOB_STATUS_LABELS: Record<CrewJobStatus, string> = {
  draft: 'Draft',
  released: 'Released',
  filled: 'Filled',
  closed: 'Closed',
};

export type CrewJobCommitStatus =
  | 'committed'
  | 'withdrawn'
  | 'confirmed'
  | 'declined'
  | 'waitlisted'
  | 'no_show';

export const CREW_JOB_COMMIT_STATUSES: CrewJobCommitStatus[] = [
  'committed',
  'withdrawn',
  'confirmed',
  'declined',
  'waitlisted',
  'no_show',
];

export const CREW_JOB_COMMIT_STATUS_LABELS: Record<CrewJobCommitStatus, string> = {
  committed: 'Pending',
  withdrawn: 'Withdrawn',
  confirmed: 'On roster',
  declined: 'Declined',
  waitlisted: 'Waitlisted',
  no_show: 'No-show',
};

export interface CrewJob {
  id: string;
  slug: string;
  title: string;
  location: string;
  locationLat?: number | null;
  locationLng?: number | null;
  hotelName?: string | null;
  hotelLocation?: string | null;
  hotelLat?: number | null;
  hotelLng?: number | null;
  description: string;
  startDate: Date | null;
  endDate: Date | null;
  /** When set, startDate is resolved from this project task's startDate. */
  startTaskId?: string | null;
  /** When set, endDate is resolved from this project task's endDate (may differ from startTaskId). */
  endTaskId?: string | null;
  crewNeeded: number;
  payUnit?: CrewJobPayUnit | null;
  payMin?: number | null;
  payMax?: number | null;
  projectId?: string | null;
  dealId?: string | null;
  projectName?: string | null;
  status: CrewJobStatus;
  releasedAt?: Date | null;
  releasedBy?: string | null;
  /** When staff closed the raise (null while open). */
  closedAt?: Date | null;
  /** Uid of the staff member who closed the raise. */
  closedBy?: string | null;
  /** Display name of who closed the raise (best-effort). */
  closedByName?: string | null;
  /** Confirmed roster size. Pending website signups do not increment this. */
  commitCount: number;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export type CrewJobCommitSource = 'website' | 'crm';

export interface CrewJobCommit {
  id: string;
  jobId: string;
  jobSlug: string;
  jobTitle: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  note: string;
  status: CrewJobCommitStatus;
  source: CrewJobCommitSource;
  contractorUserId?: string | null;
  internalNotes?: string;
  submittedAt: Date;
}

/** Public list item returned by listReleasedCrewJobs. */
export interface ReleasedCrewJobListItem {
  /** Firestore doc id when returned by the public API. */
  id?: string;
  slug: string;
  title: string;
  location: string;
  startDate: string | null;
  endDate: string | null;
  crewNeeded: number;
  commitCount: number;
  spotsLeft: number;
  status: 'released' | 'filled';
  projectName?: string | null;
  payUnit?: CrewJobPayUnit | null;
  payMin?: number | null;
  payMax?: number | null;
}

export interface ReleasedCrewJobDetail extends ReleasedCrewJobListItem {
  description: string;
  hotelName?: string | null;
  hotelLocation?: string | null;
  locationLat?: number | null;
  locationLng?: number | null;
  hotelLat?: number | null;
  hotelLng?: number | null;
}
